// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import Benefits from '../../home/components/HomeLanding/Benefits/Benefits';
import AlertContainer from '../../alert/container/AlertContainer';
import { FormValidator } from '../../../utils/form';
import classNames from 'classnames/bind';
import Modal from '../../../elements/Modal/Modal';
import formStyle from '../../../assets/styles/form.scss';
import InputQuantityRound from '../../../elements/InputQuantityRound/InputQuantityRound';
import FreqQuestions from '../../../elements/FreqQuestions/FreqQuestions';
import SuccessErrorModal from '../../../elements/SuccessErrorModal/SuccessErrorModal';

import style from './Cargo.scss';
import cargoBoat from '../../../assets/img/cargo/cargo_boat.png';
import buttonStyle from '../../../assets/styles/button.scss';
import icons from '../../../assets/styles/icons.scss';
import inputStyle from '../../../assets/styles/input.scss';

import type { FormValidationResult } from '../../../utils/form';
import type { InputQuantityAction } from '../../../elements/InputQuantity/InputQuantity';
import type { User } from '../../../entities';
import type { HTTPError } from '../../../error';
import type { ContactMessageRequest } from '../duck';
import type { Question } from '../../../elements/FreqQuestions/FreqQuestions';
import type { LocaleState } from '../../locale/duck';

type Props = {
  +localeState: LocaleState;
  +handleOpenAuthModal: (openLoginView?: boolean) => void;
  +authModalVisible?: boolean;
  +user?: User;
  +postContactMessageError?: HTTPError;
  +cargoPostContactMessage: (contactMessage: ContactMessageRequest) => void;
  +isPostingContactMessage?: boolean;
  +postContactMessageSuccess?: boolean;
};

type State = {
  products: CargoProduct[];
  newModalOpen: boolean;
  deleteModalOpen: boolean;
  deleteIndex?: number;
  editIndex?: number;

  url: {
    value: string;
    errors?: string[];
  };
  description: {
    value?: string;
    errors?: string[];
  };
  amount: {
    value: number;
    errors?: string[];
  };
  successModalOpen: boolean;
};

type CargoProduct = {
  url: string;
  amount: number;
  description: string;
};

const INITIAL_FORM_STATE = {
  url: {
    value: '',
  },
  description: {
    value: '',
  },
  amount: {
    value: 1,
  },
};

const questionsIntlMap = {
  first: {
    title: 'cargo.faq.first.title',
    content: 'cargo.faq.first.content',
  },
  second: {
    title: 'cargo.faq.second.title',
    content: 'cargo.faq.second.content',
  },
  third: {
    title: 'cargo.faq.third.title',
    US: {
      content: 'cargo.faq.third.content.PE',
    },
    PE: {
      content: 'cargo.faq.third.content.PE',
    },
    CO: {
      content: 'cargo.faq.third.content.CO',
    },
  },
  fourth: {
    title: 'cargo.faq.fourth.title',
    content: 'cargo.faq.fourth.content',
  },
};


const messages = defineMessages({
  amount: {
    id: 'cargo.item.amount',
  },
});

export default class Cargo extends Component<Props, State> {
  formValidator: FormValidator;
  pendingAction: ?('add' | 'submit');

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      url: {
        required: 'required',
      },
      /*description: {
        required: 'required',
      },*/
    });
    this.state = {
      ...INITIAL_FORM_STATE,
      products: [],
      newModalOpen: false,
      deleteModalOpen: false,
      successModalOpen: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    const { user, authModalVisible, postContactMessageSuccess } = this.props;
    if (this.pendingAction && !!user) {
      if (this.pendingAction === 'add') {
        this.handleOpenNewModal();
      } else if (this.pendingAction === 'submit') {
        this.handleSubmit();
      }
    }

    if (authModalVisible === false && prevProps.authModalVisible === true) {
      this.pendingAction = undefined;
    }

    if (!prevProps.postContactMessageSuccess && postContactMessageSuccess) {
      this.setState({
        successModalOpen: true,
        products: [],
      });
    }
  }

  handleOpenNewModal = () => {
    const { user, handleOpenAuthModal } = this.props;
    if (!!user) {
      this.setState({ ...INITIAL_FORM_STATE, newModalOpen: true });
    } else {
      handleOpenAuthModal(true);
      this.pendingAction = 'add';
    }
  };

  handleOpenEditModal = (e: Event, editIndex: number) => {
    e.preventDefault();
    this.setState((state: State) => ({
      newModalOpen: true,
      editIndex,
      url: { value: state.products[editIndex].url },
      amount: { value: state.products[editIndex].amount },
      description: { value: state.products[editIndex].description },
    }));
  };

  handleCloseNewModal = () => this.setState({ newModalOpen: false, editIndex: undefined });

  handleOpenDeleteModal = (e: Event, deleteIndex: number) => {
    e.preventDefault();
    this.setState({
      deleteModalOpen: true,
      deleteIndex,
    });
  };

  handleCloseDeleteModal = () => this.setState({ deleteModalOpen: false, deleteIndex: undefined });

  handleCloseSuccessModal = () => this.setState({ successModalOpen: false });

  handleAddProduct = () => {
    const { url, description, amount, editIndex } = this.state;
    const errors = this.formValidator.validateForm({
      url: url.value,
      // description: description.value,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) => {
        this.setState({
          [error.field]: {
            ...this.state[error.field],
            errors: error.errors,
          },
        });
      });
    } else {
      let changes = {};
      if (typeof editIndex === 'number') {
        changes = {
          products: {
            [editIndex]: {
              $set: {
                url: url.value,
                description: description.value,
                amount: amount.value
              },
            },
          },
          newModalOpen: { $set: false },
          $unset: ['editIndex'],
        }
      } else {
        changes = {
          products: {
            $push: [
              {
                url: url.value,
                description: description.value,
                amount: amount.value
              }
            ]
          },
          newModalOpen: { $set: false },
        };
      }
      this.setState(update(this.state, changes));
    }
  };

  handleDeleteProduct = (index: number) => {
    this.setState(update(this.state, {
      products: { $splice: [[index, 1]] },
      deleteModalOpen: { $set: false },
      $unset: ['deleteIndex'],
    }));
  };

  handleSubmit = () => {
    const { user, handleOpenAuthModal, cargoPostContactMessage } = this.props;
    const { products } = this.state;
    if (!!user) {
      const message = products.map((product: CargoProduct) =>
        `URL: ${product.url}\nAmount: ${product.amount}\nDescription: ${product.description}`)
        .join('\n------------------------------------\n');
      const req = {
        email: user.email,
        subject: `Quote for cargo - from ${user.firstName} ${user.lastName}`,
        message,
      };
      console.log('send', req);
      cargoPostContactMessage(req);
    } else {
      handleOpenAuthModal(true);
      this.pendingAction = 'submit';
    }
  };

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleChangeAmount = (action: InputQuantityAction) => {
    if (action === 'more') {
      this.setState((state: State) => ({
        amount: {
          value: state.amount.value + 1,
        },
      }));
    } else if (action === 'less') {
      this.setState((state: State) => ({
        amount: {
          value: Math.max(state.amount.value - 1, 1),
        },
      }));
    }
  };

  render() {
    const {
      newModalOpen, deleteModalOpen, deleteIndex, products, editIndex, successModalOpen,
      url, description, amount,
    } = this.state;
    const { isPostingContactMessage } = this.props;

    const urlError = url && url.errors && FormValidator.buildErrorLabel(url.errors, {
      required: 'newOrder.scrap.error.required',
      urlFormat: 'newOrder.scrap.error.format',
    });

    /*const descriptionError = description && description.errors && FormValidator.buildErrorLabel(description.errors, {
      required: 'cargo.addModal.description.error',
    });*/
    const hasProducts = products.length > 0;

    const questions: Question[] = Object.keys(questionsIntlMap).map((key: string) => {
      if (!questionsIntlMap[key].title || !questionsIntlMap[key].content) {
        const { localeState } = this.props;
        return ({
          labelIntl: questionsIntlMap[key].title,
          contentIntl: questionsIntlMap[key][localeState.domainCountry || 'PE'].content,
        });
      }
      return ({
        labelIntl: questionsIntlMap[key].title,
        contentIntl: questionsIntlMap[key].content,
      });
    });
    return (
      <Fragment>
        <section className={style.cargoCommonWrap}>
          <AlertContainer showId={['cargo']}/>
          <FormattedMessage id="cargo.title">{
            txt => <h1 className={style.title}>{txt}</h1>
          }</FormattedMessage>
          <section className={style.imgWrap}>
            <img className={style.cargoBoat}
                 src={cargoBoat}
                 alt="cargo_boat"/>
          </section>
        </section>
        <section className={style.wideWrap}>
          <section className={classNames(style.cargoCommonWrap, { [style.noProducts]: !hasProducts })}>
            <section className={style.buttonsWrap}>
              <FormattedMessage id="cargo.button.add">{
                txt => <button onClick={() => this.handleOpenNewModal()}
                               className={classNames(buttonStyle.btn, buttonStyle.lightBlueInverse)}>
                  <i className={icons.iconPlusLight}/>{txt}</button>
              }</FormattedMessage>
              {hasProducts && <FormattedMessage id="cargo.button.quote">{
                txt => <button onClick={() => this.handleSubmit()}
                               className={classNames(buttonStyle.btn, buttonStyle.orange)}>{txt}</button>
              }</FormattedMessage>}
            </section>
            {hasProducts &&
            <section className={style.productsWrap}>
              {products.map((product: CargoProduct, n: number) =>
                <section key={n}
                         className={style.productWrap}>
                  <a className={style.url}
                     href={product.url}
                     target="_blank">{product.url}</a>
                  <p
                    className={style.amount}>{this.context.intl.formatMessage(messages.amount)}: <span>{product.amount}</span>
                  </p>
                  <FormattedMessage id="cargo.item.description">{
                    txt => <p className={style.description}>{txt}: {product.description}</p>
                  }</FormattedMessage>
                  <section className={style.options}>
                    <FormattedMessage id="cargo.item.edit">{
                      txt => <a onClick={(e: Event) => this.handleOpenEditModal(e, n)}
                      ><i className={icons.iconEditLight}/>{txt}</a>
                    }</FormattedMessage>
                    <FormattedMessage id="cargo.item.delete">{
                      txt => <a onClick={(e: Event) => this.handleOpenDeleteModal(e, n)} className={style.delete}>
                        <i className={icons.iconTrashcanLight}/>{txt}</a>
                    }</FormattedMessage>
                  </section>
                </section>)}
            </section>}
          </section>
        </section>
        <section className={classNames(style.cargoCommonWrap, style.benefitsWrap)}>
          <Benefits/>
        </section>
        <section className={style.cargoCommonWrap}>
          <FreqQuestions titleIntl="cargo.faq.title"
                         questions={questions}/>
        </section>

        <Modal open={newModalOpen}
               big
               handleClose={() => this.handleCloseNewModal()}>
          <section className={style.modalWrap}>
            <FormattedMessage id="cargo.addModal.title">{
              txt => <h2 className={style.modalTitle}>{txt}</h2>
            }</FormattedMessage>

            <section className={formStyle.formWrap}>
              <section className={formStyle.row}>
                <article className={classNames(formStyle.col, style.col)}>
                  <FormattedMessage id="cargo.addModal.url.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <FormattedMessage id="cargo.addModal.url.placeholder">{
                    txt => <input type="text"
                                  name="url"
                                  className={classNames(inputStyle.input, {
                                    [inputStyle.lightBlue]: url.value,
                                    [inputStyle.red]: url.errors && url.errors.length,
                                  })}
                                  value={url.value}
                                  placeholder={txt}
                                  onFocus={this.handleFocus}
                                  onChange={this.handleChange}/>
                  }</FormattedMessage>
                  {urlError}
                </article>
                <article className={classNames(formStyle.col, style.col)}>
                  <FormattedMessage id="cargo.addModal.description.label">{
                    txt => <label className={formStyle.label}>{txt}</label>
                  }</FormattedMessage>
                  <FormattedMessage id="cargo.addModal.description.placeholder">{
                    txt => <textarea name="description"
                                     autoComplete="disabled"
                                     value={description.value}
                                     className={classNames(inputStyle.input, {
                                       [inputStyle.lightBlue]: description && description.value,
                                       // [inputStyle.red]: description.errors && description.errors.length,
                                     })}
                                     onChange={this.handleChange}
                                     onFocus={this.handleFocus}
                                     placeholder={txt}/>
                  }</FormattedMessage>
                  {/*descriptionError*/}
                </article>
                <article className={classNames(formStyle.col, style.col)}>
                  <FormattedMessage id="cargo.addModal.amount.label">{
                    txt => <label className={formStyle.label}>{txt}</label>
                  }</FormattedMessage>
                  <InputQuantityRound handleChange={this.handleChangeAmount}
                                      quantity={`${amount.value}`}/>
                </article>
              </section>
            </section>
            <FormattedMessage
              id={typeof editIndex === 'undefined' ? "cargo.addModal.button" : "cargo.addModal.button.save"}>{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.modalButton)}
                             onClick={() => this.handleAddProduct()}>{txt}</button>
            }</FormattedMessage>
          </section>
        </Modal>

        <Modal open={deleteModalOpen}
               big
               handleClose={() => this.handleCloseDeleteModal()}>
          <section className={style.modalWrap}>
            <FormattedMessage id="cargo.deleteModal.title">{
              txt => <h2 className={style.modalTitle}>{txt}</h2>
            }</FormattedMessage>
            <FormattedMessage id="cargo.deleteModal.button.delete">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.red, style.modalButton)}
                             onClick={() => typeof deleteIndex === 'number' &&
                               this.handleDeleteProduct(deleteIndex)}>{txt}</button>
            }</FormattedMessage>
            <FormattedMessage id="cargo.deleteModal.button.cancel">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.darkGrey, style.modalButton)}
                             onClick={() => this.handleCloseDeleteModal()}>{txt}</button>
            }</FormattedMessage>
          </section>
        </Modal>
        <SuccessErrorModal isOpen={successModalOpen || isPostingContactMessage}
                           handleCloseModal={this.handleCloseSuccessModal}
                           titleInt='cargo.successModal.title'
                           color='lightBlue'
                           isLoading={isPostingContactMessage}
                           success/>
      </Fragment>
    );
  }
}

Cargo.propTypes = {
  handleOpenAuthModal: PropTypes.func.isRequired,
  authModalVisible: PropTypes.bool,
  user: PropTypes.object,
  postContactMessageError: PropTypes.object,
  cargoPostContactMessage: PropTypes.func.isRequired,
  isPostingContactMessage: PropTypes.bool,
  postContactMessageSuccess: PropTypes.bool,
};

Cargo.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/cargo/components/Cargo.jsx